import { LoadingButton } from '@mui/lab';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import axios from 'axios';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startSubscriptionPeriod } from 'src/api/stoApi';
import { Colors } from 'src/components/utils/Theme';
import { Asset } from 'src/models';

interface Props {
  open: boolean;
  asset: Asset;
  handleClose: () => void;
  triggerRefresh: () => void;
}

export const StartSubscriptionPeriodDialog: FC<Props> = ({ open, asset, handleClose, triggerRefresh }) => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState<'init' | 'error' | 'success'>('init');
  const [error, setError] = useState<string>('');

  const handleCloseAndReset = () => {
    handleClose();
    setDialogState('init');
    setError('');
  };

  const requestApproval = async () => {
    setLoading(true);
    try {
      const startSubscription = await startSubscriptionPeriod(asset.id);
      if (startSubscription.status === 200) {
        setDialogState('success');
        triggerRefresh();
      }
    } catch (error) {
      setDialogState('error');
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data.error);
        if (error.response.data.message) {
          setError(error.response.data?.message);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      {dialogState === 'init' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.startsubscriptionperiod.init.headline')}</DialogTitle>
          <DialogContent sx={{ minWidth: 500 }}>
            <Typography>{t('dashboard.assets.dialog.startsubscriptionperiod.init.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={handleClose}>
              {t('dashboard.assets.dialog.startsubscriptionperiod.init.cancel')}
            </Button>
            <LoadingButton variant="contained" disableElevation color="primary" loading={isLoading} onClick={requestApproval}>
              {t('dashboard.assets.dialog.startsubscriptionperiod.init.confirm')}
            </LoadingButton>
          </DialogActions>
        </>
      )}
      {dialogState === 'error' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.startsubscriptionperiod.error.headline')}</DialogTitle>
          <DialogContent sx={{ minWidth: 500 }}>
            <Typography>{t('dashboard.assets.dialog.startsubscriptionperiod.error.message')}</Typography>
            <Typography color={Colors.ERROR}>{error}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.startsubscriptionperiod.error.ok')}
            </Button>
          </DialogActions>
        </>
      )}
      {dialogState === 'success' && (
        <>
          <DialogTitle>{t('dashboard.assets.dialog.startsubscriptionperiod.success.headline')}</DialogTitle>
          <DialogContent sx={{ minWidth: 500 }}>
            <Typography>{t('dashboard.assets.dialog.startsubscriptionperiod.success.message')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" disableElevation color="primary" onClick={handleCloseAndReset}>
              {t('dashboard.assets.dialog.startsubscriptionperiod.success.ok')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
